import { ServiceName, MultiImagePublishableServices } from '@deltasierra/integrations-integration-types';
import { Brand, createBranded } from '@deltasierra/type-utilities';
import { oneLineTrim } from 'common-tags';
import { PrintUnits, convertUnits } from '../builderDocument';
import { FullAuditable, FullAuditableKeys, HasId, Omit } from '../common';
import { BasicPlatform, Platform } from '../platform/Platform';

export type BuilderTemplateFormatId = Brand<number, 'BuilderTemplateFormatId'>;
export const BuilderTemplateFormatId = createBranded<BuilderTemplateFormatId>();

export interface BuilderTemplateFormat extends HasId<BuilderTemplateFormatId>, FullAuditable {
    groupLabel: string;
    label: string;
    width: number;
    height: number;
    canPublishTo: ServiceName | null;
    animatable: boolean;
    type: BuilderTemplateFormatType;
    platformId: number;
    platform?: Platform;
}

export type BuilderTemplateFormatType = 'email' | 'image' | 'print' | 'video';

export type BasicBuilderTemplateFormat = Omit<BuilderTemplateFormat, FullAuditableKeys | 'platform'> & {
    platform: BasicPlatform;
};

interface SizeOption {
    label?: string;
    width: number;
    height: number;
}

interface FormatSizeOptionConfig {
    isPrintDocument?: boolean;
    userPreferencePrintUnits?: PrintUnits;
}

interface DimensionsWithUnits {
    width: number;
    height: number;
    units: PrintUnits;
}

export function getFullLabelForFormat(
    builderDocumentFormat: Pick<BuilderTemplateFormat, 'groupLabel' | 'label'>,
): string {
    return `${builderDocumentFormat.groupLabel} - ${builderDocumentFormat.label}`;
}

export function formatSizeOption(
    option: SizeOption,
    config: FormatSizeOptionConfig = {},
    withParenthesis = true,
): string {
    const dimensions: DimensionsWithUnits = getDimensionsForConfig(option, config);
    const decimalPlaces = dimensions.units === PrintUnits.inches ? 2 : 0;
    if (option.label) {
        return `${option.label}${formatDimensionsWithUnits(dimensions, withParenthesis, decimalPlaces)}`;
    } else {
        return formatDimensionsWithUnits(dimensions, withParenthesis, decimalPlaces);
    }
}

function formatDimensionsWithUnits(dimensions: DimensionsWithUnits, withParenthesis = true, decimalPlaces = 0) {
    if (withParenthesis) {
        return oneLineTrim`
            (
                ${dimensions.width.toFixed(decimalPlaces)}${dimensions.units} x 
                ${dimensions.height.toFixed(decimalPlaces)}${dimensions.units}
            )
        `;
    } else {
        return oneLineTrim`
            ${dimensions.width.toFixed(decimalPlaces)}${dimensions.units} x 
            ${dimensions.height.toFixed(decimalPlaces)}${dimensions.units}
        `;
    }
}

function getDimensionsForConfig(option: SizeOption, config: FormatSizeOptionConfig): DimensionsWithUnits {
    const fallbackUnits = PrintUnits.millimetres;
    const units = config.isPrintDocument ? config.userPreferencePrintUnits || fallbackUnits : PrintUnits.pixels;
    const width = convertUnits(option.width, PrintUnits.pixels, units);
    const height = convertUnits(option.height, PrintUnits.pixels, units);
    return { height, units, width };
}

export function formatBuilderTemplateFormat(
    builderTemplateFormat: Pick<BuilderTemplateFormat, 'groupLabel' | 'height' | 'label' | 'type' | 'width'>,
): string {
    return formatSizeOption(
        {
            height: builderTemplateFormat.height,
            label: getFullLabelForFormat(builderTemplateFormat),
            width: builderTemplateFormat.width,
        },
        {
            isPrintDocument: builderTemplateFormat.type === 'print',
        },
    );
}

export function platformSupportsMultiImage(serviceName: ServiceName): boolean {
    return MultiImagePublishableServices.filter(service => service === serviceName).length > 0;
}
